/**
 * Canonical pricing constants — single source of truth for all price, credit,
 * and overage-rate numbers across backend and frontend.
 *
 * Cache scope: static module-level constants; imported once at module load.
 *
 * Rules:
 * - Add new constants here; never re-declare them in HARDCODED_DEFAULTS,
 *   planConfig.ts, or credit-rates.ts.
 * - HARDCODED_DEFAULTS in billing-config.service.ts imports these.
 * - Frontend planConfig.ts and credit-rates.ts consume these (Steps 5–6).
 * - A test asserts the latest pricing_plans seed migration matches these values.
 */

import assistantImagePricing from '../../../config/assistant-image-pricing.json';
import creditPricingFile from '../../../config/credit-pricing.json';
import { CONVERSATION_LEVEL_CREDIT_MULTIPLIER, type ConversationLevel } from './chat-rooms';

/** Shared with Python's image tool: one price owner for quotes, prompts and charges. */
export const ASSISTANT_IMAGE_CREDITS = assistantImagePricing.credits;
export type AssistantImageQuality = keyof typeof ASSISTANT_IMAGE_CREDITS;
export const ASSISTANT_IMAGE_PRICING_POLICY_VERSION = assistantImagePricing.policyVersion;

// ── Plan prices (USD cents) ──────────────────────────────────────────────────
export const PLAN_PRICES = {
  pro: {
    monthlyCents: 49000,   // $490
    annualCents: 490000,   // $4,900
  },
  enterprise: {
    monthlyCents: 249000,  // $2,490
    annualCents: 2988000,  // 12 x $2,490, no annual discount (the old "save 2 months" model is retired).
    // getLsCheckoutPriceCents in apps/backend/src/billing/billing.constants.ts still encodes the
    // legacy $24,900 Lemon Squeezy variant price: that is the live checkout path, deliberately
    // left unchanged while checkout is disabled, so the two numbers now intentionally disagree.
  },
} as const;

// ── Credits per plan ─────────────────────────────────────────────────────────
export const PLAN_CREDITS = {
  trial: 300,
  pro: 1250,          // per seat
  enterprise: 10000,  // org pool
} as const;

// ── Overage rates (cents per credit) ────────────────────────────────────────
// Retired as a rate source: the overage rate is the per-org value below
// (DEFAULT_CREDIT_DISPLAY_VALUE_CENTS). Kept for plan settings / migration parity.
/** Monthly overage rate, cents per credit. Pro 39¢, Enterprise 25¢. */
export const OVERAGE_RATE_CENTS_MONTHLY = {
  pro: 39,
  enterprise: 25,
} as const;

/**
 * Annual overage rate, cents per credit. Pro 33¢ ($4,900÷15,000). Enterprise 21¢: no longer
 * derivable from a current annual price (the legacy $24,900÷120,000 basis is retired). Kept at
 * 21 because real annual customers are still billed at this rate (Critical Decision A1).
 */
export const OVERAGE_RATE_CENTS_ANNUAL = {
  pro: 33,
  enterprise: 21,
} as const;

// ── Overage credit rate (per org) ────────────────────────────────────────────
/**
 * Default overage credit rate, in cents per credit, for every org: 10,000 credits are
 * worth $5,000. A per-org backoffice setting (`billing_accounts.credit_display_value_cents`,
 * name kept from its display-only origin). It is the single rate source for overage, revenue
 * attribution and what the account sees; the per-tier OVERAGE_RATE_CENTS_* above are retired
 * as a rate source.
 */
export const DEFAULT_CREDIT_DISPLAY_VALUE_CENTS = 50;
/** Backoffice bounds for the per-org display value, in cents. */
export const CREDIT_DISPLAY_VALUE_CENTS_MIN = 1;
export const CREDIT_DISPLAY_VALUE_CENTS_MAX = 100000;

// ── Per-action credit costs ──────────────────────────────────────────────────
/** Credits charged per respondent by audience tier. */
export const CREDITS_PER_RESPONDENT: Record<string, number> = {
  tier1: 1,
  tier2: 6,
  tier3: 20,
};

/** Credits charged per chat message (1:1) by audience tier. */
export const CHAT_CREDITS_PER_MSG: Record<string, number> = {
  tier1: 1,
  tier2: 2,
  tier3: 3,
};

/** Credits charged per focus-group room message by audience tier. */
export const ROOM_CREDITS_PER_MSG: Record<string, number> = {
  tier1: 2,
  tier2: 3,
  tier3: 4,
};

/**
 * Credits for one conversation message: `baseRate × multiplier`. `baseRate` is the room's
 * resolved audience rate (1:1 or focus-group), `multiplier` comes from
 * `CONVERSATION_LEVEL_CREDIT_MULTIPLIER` or `CONVERSATION_TIER_CREDIT_MULTIPLIER`.
 * Preview (frontend) and charge (Nest) both call it so they cannot drift.
 * No rounding: integer rates times the integer multipliers stay integers, and a fractional
 * audience rate keeps today's fractional charge, multiplied. Non-finite or negative inputs
 * collapse to 0 (base) and 1 (multiplier). Cache scope: one-shot pure function.
 */
export function conversationMessageCost(baseRate: number, multiplier: number): number {
  const base = Number.isFinite(baseRate) ? Math.max(0, baseRate) : 0;
  const factor = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
  return base * factor;
}

/** Credits charged when creating an audience (per twin). */
export const AUDIENCE_CREATION_CREDITS = 1;

/**
 * Full-study credit cost: `effectiveRate × billableUnits`.
 *
 * `billableUnits` comes from `billableUnitsForDesign` in `study-design.ts`
 * (`numRespondents × cells`; cells = 1 for comparative and for the retired
 * true_monadic (TRUE_MONADIC_RETIRED: persisted studies only), N for
 * twin_monadic). Preview (frontend) and charge (orchestration) both call this
 * so they cannot drift — same parity pattern as `quickStudyQuestionCost`.
 *
 * `studyMode === 'quick'` halves the per-respondent rate with ceil (legacy
 * quick-study-on-full-pipeline discount). Cache scope: one-shot pure function.
 */
export function studyCreditCost(input: {
  creditsPerRespondent: number;
  billableUnits: number;
  studyMode?: "full" | "quick";
}): number {
  const baseRate = Number.isFinite(input.creditsPerRespondent)
    ? Math.max(0, input.creditsPerRespondent)
    : 0;
  const units = Number.isFinite(input.billableUnits)
    ? Math.max(0, Math.floor(input.billableUnits))
    : 0;
  const effectiveRate =
    input.studyMode === "quick" ? Math.ceil(baseRate / 2) : baseRate;
  return effectiveRate * units;
}

/**
 * Quick Study charge rate in credits PER READ, by audience tier. A read = one
 * respondent × one question-unit × one creative cell.
 *
 * `tier1` (basic) and `tier2` (premium) are flat at 0.3 — equal to tier2 full-study
 * per-respondent parity (6 cr/respondent ÷ a 15-question study). `tier3` (exclusive)
 * is premium-priced at 0.8. This map is the single tuning point: re-pricing a tier is
 * a one-line change with no call-site edits. It is deliberately DECOUPLED from
 * `CREDITS_PER_RESPONDENT` so Quick Study and full-study pricing move independently.
 *
 * Keyed by `string` (not `AudienceTier`) to match the other rate maps above and to
 * avoid requiring keys for the legacy `standard`/`premium` union members — callers
 * normalize the tier (`normalizePersistedAudienceTier`) before indexing.
 */
export const QUICK_STUDY_CREDITS_PER_READ: Record<string, number> = {
  tier1: 0.3,
  tier2: 0.3,
  tier3: 0.8,
};

/** Floor for any single Quick Study question — never charge 0. */
export const QUICK_STUDY_MIN_CREDITS_PER_QUESTION = 1;

/**
 * Reads billed when a user cancels a running Quick Study question. A flat fee (in
 * reads, priced at the tier rate) that recovers partial compute and removes the
 * "watch the partial streamed results, then cancel for free" loophole under the
 * charge-on-success model.
 */
export const QUICK_STUDY_CANCEL_FEE_READS = 10;

/**
 * Total billable reads for one Quick Study question: `twins × creativeCells × questionUnits`.
 * - `creativeCells` — number of creatives shown (monadic fans out one pass per creative;
 *   default 1 for a single-stimulus / text question).
 * - `questionUnits` — sub-questions billed per twin (a Van Westendorp PSM question bills
 *   `PSM_QUESTION_COUNT` = 4; default 1).
 * Non-finite / negative inputs collapse to 0; missing multipliers default to 1.
 */
export function quickStudyReads(input: {
  twins: number;
  creativeCells?: number;
  questionUnits?: number;
}): number {
  const norm = (n: number | undefined, fallback: number): number =>
    Number.isFinite(n) ? Math.max(0, Math.floor(n as number)) : fallback;
  return norm(input.twins, 0) * (norm(input.creativeCells, 1) || 1) * (norm(input.questionUnits, 1) || 1);
}

/**
 * Credits for one Quick Study sample question: `max(1, floor(rate × reads))`.
 * When `rate` is omitted, it comes from `QUICK_STUDY_CREDITS_PER_READ` (an unknown
 * tier falls back to the tier2 default). A passed `rate` skips that map, including
 * the tier2 fallback — the caller already resolved it (a frozen run rate).
 * Floors (favors the user) and never charges below the min. Compute `reads` with
 * `quickStudyReads(...)` so the monadic and PSM multipliers are applied
 * consistently on both the frontend preview and the backend charge.
 * Two-argument callers stay valid.
 */
export function quickStudyQuestionCost(
  tier: string,
  reads: number,
  rate?: number,
): number {
  const resolved =
    typeof rate === "number" && Number.isFinite(rate)
      ? rate
      : (QUICK_STUDY_CREDITS_PER_READ[tier] ??
        QUICK_STUDY_CREDITS_PER_READ.tier2);
  const r = Number.isFinite(reads) ? Math.max(0, Math.floor(reads)) : 0;
  return Math.max(
    QUICK_STUDY_MIN_CREDITS_PER_QUESTION,
    Math.floor(resolved * r),
  );
}

/**
 * Flat fee (credits) charged when a running Quick Study question is cancelled:
 * `QUICK_STUDY_CANCEL_FEE_READS` reads priced at the tier rate (tier2 → 3, tier3 → 8).
 */
export function quickStudyCancelFee(tier: string): number {
  return quickStudyQuestionCost(tier, QUICK_STUDY_CANCEL_FEE_READS);
}

// ── Quick Study billable-TWIN counting (shared FE preview ↔ backend charge) ────
// These three pure functions were previously FE-only in `quickStudyPure.ts`; they
// move here (RND run relay, Step 6) so the backend `/quick-study/execute` relay
// computes the billable `twins` count with the SAME math the FE credit-estimate
// display uses — parity by construction, not by mirrored code. `quickStudyPure.ts`
// re-exports them so its import block and existing test suite are untouched; that
// green suite is the parity test for the moved math. Do not re-declare either
// side — one source of truth (rule: never two human-edited sources without a
// parity check).

/**
 * Sub-questions billed per twin for a Van Westendorp PSM question. The FE's
 * `PSM_PRICE_QUESTIONS.length` MUST equal this literal — a drift tripwire test
 * asserts it FE-side (`quickStudyPure` keeps the question list). Non-PSM
 * questions bill 1 unit; see `quickStudyReads`'s `questionUnits`.
 */
export const PSM_QUESTION_COUNT = 4;

/**
 * The per-version billable TWIN count for a monadic run: `min(sampleSize, union)`
 * — python fills each version to `n_per_creative = min(panel_per_version, len(union))`
 * and reads that many per version (twin-monadic reuse covers a thin union), so
 * `reads = twins × creativeCells × questionUnits` (with the ×versions carried by
 * `creativeCells`) equals delivered reads. A non-true-union run (scoped/segment
 * monadic, or non-monadic) passes the plain drawn panel (`unionSize`) through
 * verbatim. Registering the raw union total on a true-monadic run would
 * double-count to n×V².
 */
export function monadicBillableTwins(
  isMonadicRun: boolean,
  sampleSize: number,
  unionSize: number,
): number {
  return isMonadicRun ? Math.min(sampleSize, unionSize) : unionSize;
}

/**
 * Count a drawn union's personas per STRATUM: baseline twins (empty `labels`) key
 * on `""`, a cut twin on its single label. Measured from the ACTUAL post-claiming
 * draw so billing measures from delivery, never the sizing-time estimate. The
 * backend passes `{ labels: persona.segment_labels }` (mirroring python's
 * `segment_labels?.[0] ?? ''` stratum key).
 */
export function stratumCountsFromUnion(
  drawn: ReadonlyArray<{ labels?: ReadonlyArray<string> | null }>,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const d of drawn) {
    const key = d.labels && d.labels.length ? d.labels[0] : "";
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/**
 * `Σₛ min(n_s, available_s)` — the per-version billable twin count for a STRATIFIED
 * (combined monadic + subsegment) run, measured from the actual drawn union counts.
 * `reads = this × versions` (× via `creativeCells`) equals python's per-version
 * delivery (`Σₛ min(n_s, L_s)`) by construction — panel, bill, and delivery agree.
 * NOT `min(Σn_s, Σavail_s)`, which over-bills.
 */
export function stratifiedBillableTwins(
  panelByStratum: Record<string, number>,
  unionCounts: Record<string, number>,
): number {
  let sum = 0;
  for (const key of Object.keys(panelByStratum)) {
    sum += Math.min(panelByStratum[key], unionCounts[key] ?? 0);
  }
  return sum;
}

// ── Executed-reads derivation + charging policy (in-process billing, Step 7) ────

/**
 * The twin counts a completed Quick Study `result` frame carries (`QuickStudyResult`,
 * python `quick_study/models.py`). All three are GLOBAL: for a monadic run they are
 * summed across the creative fan-out (python analyzes the pooled union with
 * `total = n_per_creative × V`), and a single-pass run has one pass (creativeCells = 1).
 * So a count here is already read-level with respect to `creativeCells` on BOTH paths.
 */
export interface QuickStudyExecutedFrame {
  /** Twins that returned ANY answer (== len(answers) python builds the result from). */
  n?: number;
  /** Twins with a USABLE answer for the question type (empties/invalids excluded). */
  usable_n?: number;
  /** Twins dispatched (for a monadic run, the global n×V). */
  total?: number;
}

/**
 * EXECUTED billable reads for one completed Quick Study question — the "reads actually
 * consumed" this run delivered, derived from the terminal `result` frame. Feeds the
 * in-process charging policy below.
 *
 * ── CHARGING POLICY (Step 7 — human-reviewed billing semantics) ──
 * The run is charged `quickStudyQuestionCost(tier, min(registeredReads, executedReads))`:
 *   • executed < registered → some twins failed to answer → charge the DELIVERED reads,
 *     never the registered estimate. No surprise over-bill for reads that never happened.
 *   • executed == registered → the common fully-successful case → identical to today's
 *     charge (every dispatched twin answered).
 *   • executed > registered → NOT billed above registered (the drawn panel can legitimately
 *     exceed the registered billable count). The caller charges `registeredReads` and
 *     ALERTS on the delta at `warn` (never surprise-bills). This is the plan's "silently
 *     under-bills by design; alert on the delta" posture (Critical Decisions, C4).
 * The alert condition to wire to monitoring: a non-zero `executed − registered` delta.
 *
 * ── DERIVATION — `executedTwins × questionUnits` ──
 *   • `executedTwins` basis, in priority order:
 *       1. `n` (twins that ANSWERED) is AUTHORITATIVE whenever it is explicitly present as
 *          a finite value — INCLUDING an explicit `n === 0`. Zero answered = zero reads
 *          consumed: the run dispatched twins but none delivered a response, so it is NOT
 *          billed for dispatched-but-unanswered reads (the caller then charges
 *          `min(registered, 0) = 0`). This is the whole point: a read that returned a
 *          response is a read consumed; a dispatched-but-failed twin delivered nothing.
 *       2. `usable_n` (usable answers) — the more user-favorable fallback when `n` is
 *          absent/non-finite, used BEFORE `total` so a missing `n` never silently defaults
 *          to billing every dispatched twin.
 *       3. `total` (dispatched) — last-resort fallback (the LARGEST count) for older/edge
 *          payloads carrying neither `n` nor `usable_n`.
 *   • `× questionUnits` (PSM = 4, else 1): a fixed per-answering-twin sub-question
 *     multiplier the frame does NOT count, added back to match the registered structure.
 *   • deliberately NOT `× creativeCells`: the frame counts are already global across the
 *     monadic fan-out (and creativeCells = 1 on a single-pass run). Multiplying again
 *     would double-count to n×V².
 *
 * Returns `undefined` ONLY when no count is usable — `n` absent/non-finite AND neither
 * `usable_n` nor `total` is a positive finite (a non-`result` terminal frame, or a
 * malformed payload). The caller then charges the REGISTERED reads — never under-charging
 * to the floor on missing execution data. Note an explicit `n === 0` returns `0`, NOT
 * `undefined`: zero answered is a KNOWN execution result, not missing data.
 */
export function quickStudyExecutedReads(
  frame: QuickStudyExecutedFrame,
  questionUnits?: number,
): number | undefined {
  const units =
    Number.isFinite(questionUnits) && (questionUnits as number) > 0
      ? Math.floor(questionUnits as number)
      : 1;

  const isFiniteNum = (v: unknown): v is number =>
    typeof v === "number" && Number.isFinite(v);

  // `n` (answered) is authoritative when explicitly present, INCLUDING an explicit 0 —
  // zero answered = zero executed reads (do not fall through and bill unanswered reads).
  if (isFiniteNum(frame.n) && frame.n >= 0) {
    return Math.floor(frame.n) * units;
  }

  // `n` absent/non-finite → fall back: `usable_n` before `total` (usable answers are the
  // more user-favorable basis; `total` is the dispatched count, used only as a last resort).
  const firstPositive = (
    ...vals: Array<number | undefined>
  ): number | undefined => {
    for (const v of vals) {
      if (isFiniteNum(v) && v > 0) {
        return Math.floor(v);
      }
    }
    return undefined;
  };
  const executedTwins = firstPositive(frame.usable_n, frame.total);
  if (executedTwins === undefined) {
    return undefined;
  }
  return executedTwins * units;
}

// ── Credit metering price list (plan 2026-11-01 credit metering, Step 1) ──────
// `config/credit-pricing.json` is append-only: every published version stays, so a
// reply or message always settles at the version it was admitted under. Integer
// arithmetic only: token weights are stored in tenths (input 10, cached 1, output 50
// for the 1 : 0.1 : 5 ratio), and one Assistant reply is floored to whole
// millicredits once; the sub-millicredit remainder is returned as `absorbed`.

/** One published version of the credit price list (shape of `config/credit-pricing.json`). */
export interface CreditPricingVersion {
  version: string;
  /** ISO date (YYYY-MM-DD) the version starts to apply to new admissions. */
  effectiveFrom: string;
  assistant: {
    /** Token weights in tenths: input 10, cached 1, output 50 (1 : 0.1 : 5). */
    tokenWeightsTenths: { input: number; cached: number; output: number };
    /** Weighted tokens per credit, per level (D1). Must be a multiple of 100. */
    weightedTokensPerCredit: Record<ConversationLevel, number>;
    /** Measured typical Medium reply, in millicredits (picker estimate). */
    typicalMediumTurnMillicredits: number;
    /** Every Assistant profile id, staff included: its rate level and reply limit. */
    profiles: Record<string, {
      level: ConversationLevel;
      replyLimitMillicredits: number;
      /** True while the limit is a placeholder awaiting owner gate G1 (shadow p99). */
      replyLimitG1Pending?: boolean;
    }>;
  };
  rooms: {
    /** Per-message base credits for enrolled accounts, by room kind and audience tier. */
    enrolledBaseCredits: Record<RoomMessageKind, Record<string, number>>;
    /** Multiplier on a message that carries a video the room has not watched (D15). */
    newVideoMultiplier: number;
  };
}

export interface CreditPricingTable {
  versions: ReadonlyArray<CreditPricingVersion>;
}

export type RoomMessageKind = 'chat' | 'focus_group';

export type CreditPricingErrorCode =
  | 'unknown_policy_version'
  | 'unknown_profile'
  | 'unknown_tier'
  | 'invalid_receipt'
  | 'invalid_price_list';

/** Typed refusal: settlement quarantines on `unknown_policy_version` rather than guessing a price. */
export class CreditPricingError extends Error {
  constructor(
    readonly code: CreditPricingErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'CreditPricingError';
  }
}

/**
 * Usage of one model call as the evaluator reads it. Step 5 extends the Python record
 * with `{invocationId, parentInvocationId, scope, cacheWrite, storageUsd, serviceFees,
 * status}`; only `scope` matters here. `inputTokens` is UNCACHED input (normalized:
 * cached tokens are not also counted in it). An absent `scope` is billable (records
 * written before Step 5 carry none); `absorbed` and `separately_priced` never count.
 */
export interface AssistantUsageReceipt {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  scope?: AssistantReceiptScope;
}

export type AssistantReceiptScope = 'billable' | 'absorbed' | 'separately_priced';

export interface AssistantUsagePrice {
  /** Whole millicredits charged, floored once over all billable receipts. */
  millicredits: number;
  /**
   * The remainder the floor waived, in tenths of a weighted token (always below one
   * millicredit's worth: `weightedTokensPerCredit / 100`). Recorded, never charged.
   */
  absorbed: number;
  policyVersion: string;
  level: ConversationLevel;
}

/** Customer-facing rate of one level, credits per million tokens (pricing page and (i) only). */
export interface AssistantLevelRate {
  inputCreditsPerMillion: number;
  cachedInputCreditsPerMillion: number;
  outputCreditsPerMillion: number;
}

function assertNonNegativeSafeInteger(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new CreditPricingError('invalid_receipt', `${what} must be a non-negative integer`);
  }
  return value;
}

function validateCreditPricingTable(table: CreditPricingTable): void {
  if (!table.versions.length) {
    throw new CreditPricingError('invalid_price_list', 'credit price list has no version');
  }
  const seen = new Set<string>();
  let previousDate = '';
  for (const entry of table.versions) {
    if (seen.has(entry.version)) {
      throw new CreditPricingError('invalid_price_list', `duplicate version ${entry.version}`);
    }
    seen.add(entry.version);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(entry.effectiveFrom) || entry.effectiveFrom < previousDate) {
      throw new CreditPricingError('invalid_price_list', `versions must be in effectiveFrom order (${entry.version})`);
    }
    previousDate = entry.effectiveFrom;
    for (const level of Object.keys(CONVERSATION_LEVEL_CREDIT_MULTIPLIER) as ConversationLevel[]) {
      const perCredit = entry.assistant.weightedTokensPerCredit[level];
      if (!Number.isSafeInteger(perCredit) || perCredit <= 0 || perCredit % 100 !== 0) {
        throw new CreditPricingError('invalid_price_list', `${entry.version}: ${level} rate must be a positive multiple of 100`);
      }
    }
  }
}

/** Version lookup and pricing over one price list. Exported so tests can pin a fixture list. */
export function createCreditPricingEvaluator(table: CreditPricingTable) {
  validateCreditPricingTable(table);
  const current = table.versions[table.versions.length - 1];

  function resolveVersion(policyVersion?: string): CreditPricingVersion {
    if (policyVersion === undefined) {
      return current;
    }
    const found = table.versions.find((entry) => entry.version === policyVersion);
    if (!found) {
      throw new CreditPricingError('unknown_policy_version', `unknown credit pricing version ${policyVersion}`);
    }
    return found;
  }

  function profileEntry(profile: string, policyVersion?: string) {
    const entry = resolveVersion(policyVersion).assistant.profiles[profile];
    if (!entry) {
      throw new CreditPricingError('unknown_profile', `no price for assistant profile ${profile}`);
    }
    return entry;
  }

  /** Price one Assistant reply from its usage receipts at a pinned version. */
  function priceAssistantUsage(input: {
    level: ConversationLevel;
    policyVersion: string;
    receipts: ReadonlyArray<AssistantUsageReceipt>;
  }): AssistantUsagePrice {
    const version = resolveVersion(input.policyVersion);
    const weights = version.assistant.tokenWeightsTenths;
    const perCredit = version.assistant.weightedTokensPerCredit[input.level];
    if (perCredit === undefined) {
      throw new CreditPricingError('invalid_receipt', `unknown level ${String(input.level)}`);
    }
    let weightedTenths = 0;
    for (const receipt of input.receipts) {
      if (receipt.scope !== undefined && receipt.scope !== 'billable') {
        continue;
      }
      weightedTenths +=
        assertNonNegativeSafeInteger(receipt.inputTokens, 'inputTokens') * weights.input +
        assertNonNegativeSafeInteger(receipt.cachedInputTokens, 'cachedInputTokens') * weights.cached +
        assertNonNegativeSafeInteger(receipt.outputTokens, 'outputTokens') * weights.output;
    }
    if (!Number.isSafeInteger(weightedTenths)) {
      throw new CreditPricingError('invalid_receipt', 'usage total out of range');
    }
    // One millicredit = perCredit / 1000 weighted tokens = perCredit / 100 tenths.
    const tenthsPerMillicredit = perCredit / 100;
    const millicredits = Math.floor(weightedTenths / tenthsPerMillicredit);
    return {
      millicredits,
      absorbed: weightedTenths - millicredits * tenthsPerMillicredit,
      policyVersion: version.version,
      level: input.level,
    };
  }

  /** The most one reply of `profile` may charge, in millicredits (G1-pending placeholders). */
  function replyLimit(profile: string, policyVersion?: string): number {
    return profileEntry(profile, policyVersion).replyLimitMillicredits;
  }

  /** The rate level an Assistant profile (staff included) is priced at. */
  function assistantProfileLevel(profile: string, policyVersion?: string): ConversationLevel {
    return profileEntry(profile, policyVersion).level;
  }

  /** Typical credits per message at `level`: the measured Medium reply scaled by rate. */
  function typicalCredits(level: ConversationLevel, policyVersion?: string): number {
    const assistant = resolveVersion(policyVersion).assistant;
    const millicredits = Math.round(
      (assistant.typicalMediumTurnMillicredits * assistant.weightedTokensPerCredit.medium) /
        assistant.weightedTokensPerCredit[level],
    );
    return millicredits / 1000;
  }

  /** Credits per million tokens at `level`, unrounded: the figure dollar amounts must be priced from. */
  function assistantLevelRateExact(level: ConversationLevel, policyVersion?: string): AssistantLevelRate {
    const assistant = resolveVersion(policyVersion).assistant;
    const perCredit = assistant.weightedTokensPerCredit[level];
    const w = assistant.tokenWeightsTenths;
    const perMillion = (weightTenths: number) => (1_000_000 * weightTenths) / (10 * perCredit);
    return {
      inputCreditsPerMillion: perMillion(w.input),
      cachedInputCreditsPerMillion: perMillion(w.cached),
      outputCreditsPerMillion: perMillion(w.output),
    };
  }

  /** Credits per million tokens at `level`, rounded to cents of a credit (display data only). */
  function assistantLevelRate(level: ConversationLevel, policyVersion?: string): AssistantLevelRate {
    const exact = assistantLevelRateExact(level, policyVersion);
    const round = (value: number) => Math.round(value * 100) / 100;
    return {
      inputCreditsPerMillion: round(exact.inputCreditsPerMillion),
      cachedInputCreditsPerMillion: round(exact.cachedInputCreditsPerMillion),
      outputCreditsPerMillion: round(exact.outputCreditsPerMillion),
    };
  }

  /**
   * Credits for one room message. Not enrolled: today's price exactly
   * (`CHAT_CREDITS_PER_MSG` / `ROOM_CREDITS_PER_MSG` or the override, times the level
   * multiplier, no video rule). Enrolled: the version's base (a per-audience override
   * still wins, D13), times the level multiplier, times `newVideoMultiplier` when the
   * message carries a video the room has not watched (D15). `levelMultiplier`, when
   * given, replaces the level's multiplier (advanced picks use
   * `CONVERSATION_TIER_CREDIT_MULTIPLIER`).
   */
  function roomMessageCredits(input: {
    kind: RoomMessageKind;
    tier: string;
    level: ConversationLevel;
    carriesNewVideo: boolean;
    enrolled: boolean;
    baseOverride?: number | null;
    levelMultiplier?: number;
    policyVersion?: string;
  }): number {
    const version = resolveVersion(input.policyVersion);
    const multiplier = input.levelMultiplier ?? CONVERSATION_LEVEL_CREDIT_MULTIPLIER[input.level];
    let base: number | undefined;
    if (typeof input.baseOverride === 'number' && Number.isFinite(input.baseOverride)) {
      base = input.baseOverride;
    } else {
      const table = input.enrolled
        ? version.rooms.enrolledBaseCredits[input.kind]
        : input.kind === 'chat' ? CHAT_CREDITS_PER_MSG : ROOM_CREDITS_PER_MSG;
      base = table?.[input.tier];
    }
    if (base === undefined) {
      throw new CreditPricingError('unknown_tier', `no ${input.kind} price for tier ${input.tier}`);
    }
    const credits = conversationMessageCost(base, multiplier);
    return input.enrolled && input.carriesNewVideo ? credits * version.rooms.newVideoMultiplier : credits;
  }

  return {
    currentVersion: current.version,
    resolveVersion,
    priceAssistantUsage,
    replyLimit,
    assistantProfileLevel,
    typicalCredits,
    assistantLevelRate,
    assistantLevelRateExact,
    roomMessageCredits,
  };
}

const creditPricing = createCreditPricingEvaluator(creditPricingFile as CreditPricingTable);

/** The version new admissions pin (the last entry of `config/credit-pricing.json`). */
export const CREDIT_PRICING_POLICY_VERSION: string = creditPricing.currentVersion;
/** Throws `CreditPricingError('unknown_policy_version')` for a version not in the list. */
export const resolveCreditPricingVersion = creditPricing.resolveVersion;
export const priceAssistantUsage = creditPricing.priceAssistantUsage;
export const replyLimit = creditPricing.replyLimit;
export const assistantProfileLevel = creditPricing.assistantProfileLevel;
export const typicalCredits = creditPricing.typicalCredits;
export const assistantLevelRate = creditPricing.assistantLevelRate;
export const assistantLevelRateExact = creditPricing.assistantLevelRateExact;
export const roomMessageCredits = creditPricing.roomMessageCredits;
